/**
 * Coordinates bulk actions: eligibility check → priority rules → execution.
 *
 * Three layers for separation of concerns (data validation, business logic, side effects).
 */
import { FC, useCallback } from 'react'

import {
  BulkActionsDefinition,
  FiltersDefinition,
  OnBulkActionCallback,
} from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'
import { Section } from 'modules/expenses/lib/RouteContextParams'

import { getCommand } from './actionCommands'
import { determineAvailableActions } from './determineAvailableActions'
import { Expensable } from './types'
import { useActionEligibility } from './useActionEligibility'
import { useBulkActionModals } from './useBulkActionModals'

interface ItemStatus {
  item: Expensable
  checked: boolean
}

interface UseExpensesBulkActionsProps {
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  canSyncWithPayroll: boolean
  canSyncWithBanking: boolean
  canRestartApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  section?: Section
}

interface UseExpensesBulkActionsReturn {
  bulkActions: BulkActionsDefinition<Expensable, FiltersDefinition<string>>
  handleBulkAction: OnBulkActionCallback<Expensable, FiltersDefinition<string>>
  Modals: FC
  // Exposed for item actions integration
  handlers: {
    onApprove: (ids: string[], overrideApprovalFlow?: boolean) => Promise<unknown>
    onReject: (ids: string[], reason?: string, overrideApprovalFlow?: boolean) => Promise<unknown>
    onSetToPaid: (ids: string[]) => Promise<unknown>
    onRestartReview: (ids: string[]) => Promise<unknown>
    onRemoveFromPayroll: (ids: string[]) => Promise<unknown>
    onRevertPaid: (ids: string[]) => Promise<unknown>
  }
  modalActions: {
    openRejectModal: (expensables: Expensable[]) => void
    openPayrollModal: (expensables: Expensable[]) => void
    openPaymentModal: (expensables: Expensable[]) => void
    openRestartApprovalModal: (expensables: Expensable[]) => void
  }
}

export const useExpensesBulkActions = ({
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  canSyncWithPayroll,
  canSyncWithBanking,
  canRestartApprovalFlow,
  canSetExpensableToPaid,
  section,
}: UseExpensesBulkActionsProps): UseExpensesBulkActionsReturn => {
  const {
    onApprove,
    onReject,
    onAddToPayroll,
    onAddToPayment,
    onRemoveFromPayroll,
    onRestartReview,
    onSetToPaid,
    onRevertPaid,
  } = useExpensableActions()

  const { computeEligibility } = useActionEligibility({
    canSyncWithPayroll,
    canSyncWithBanking,
    canRestartApprovalFlow,
    canSetExpensableToPaid,
    section,
  })

  const { openRejectModal, openPayrollModal, openPaymentModal, openRestartApprovalModal, Modals } =
    useBulkActionModals({
      currentPayrollPeriodOpen,
      availablePayrollCategories,
      onReject,
      onAddToPayroll,
      onAddToPayment,
      onRestartReview,
    })

  const getSelectedExpensables = (itemsStatus: readonly ItemStatus[]): Expensable[] =>
    itemsStatus.filter((item) => item.checked).map((item) => item.item)

  const bulkActions = useCallback<BulkActionsDefinition<Expensable, FiltersDefinition<string>>>(
    ({ itemsStatus }) => {
      const eligibility = computeEligibility(getSelectedExpensables(itemsStatus))
      return determineAvailableActions(eligibility)
    },
    [computeEligibility]
  )

  const handleBulkAction = useCallback<OnBulkActionCallback<Expensable, FiltersDefinition<string>>>(
    async (actionId, { itemsStatus }) => {
      const command = getCommand(actionId)
      if (!command) {
        return
      }

      const eligibility = computeEligibility(getSelectedExpensables(itemsStatus))

      try {
        await command.execute(
          eligibility,
          {
            onApprove,
            onReject,
            onSetToPaid,
            onRestartReview,
            onRemoveFromPayroll,
            onRevertPaid,
          },
          {
            openRejectModal,
            openPayrollModal,
            openPaymentModal,
            openRestartApprovalModal,
          }
        )
      } catch {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('expenses_v2.list.actions.action_failed'),
        })
      }
    },
    [
      computeEligibility,
      onApprove,
      onReject,
      onRemoveFromPayroll,
      onRestartReview,
      onSetToPaid,
      onRevertPaid,
      openRejectModal,
      openPayrollModal,
      openPaymentModal,
      openRestartApprovalModal,
    ]
  )

  return {
    bulkActions,
    handleBulkAction,
    Modals,
    // Expose handlers and modal actions for item actions
    handlers: {
      onApprove,
      onReject,
      onSetToPaid,
      onRestartReview,
      onRemoveFromPayroll,
      onRevertPaid,
    },
    modalActions: {
      openRejectModal,
      openPayrollModal,
      openPaymentModal,
      openRestartApprovalModal,
    },
  }
}
